/**
 * SearchForm — the mobile search field that is a REAL NATIVE FORM first and a
 * full-screen surface second.
 *
 * ## What it fixes, and why its predecessor did not
 *
 * It was `SearchTrigger`: an `EnhancedLink` whose href was
 * `routes.listings.browse(mode)`, on the argument that "pre-hydration, and with
 * JS disabled, this control is a link to the browse hub, which carries its own
 * search — rather than a dead field that swallows a tap". Both clauses of that
 * sentence were false on the only viewport the control exists on, and for the
 * same reason:
 *
 *   * `NavbarShell` is `hidden md:block`, so BELOW `md` the navbar and its
 *     omnibox do not render at all. The hub's only mobile search control was
 *     this same component — an anchor to `/listings/{mode}`. So a no-JS phone
 *     reader tapping the hero search landed on a page whose only search field
 *     was another self-link. The no-JS path was not a working page; it was a
 *     loop.
 *   * on the hub ITSELF, pre-hydration, that self-link RELOADED the page and
 *     dropped the query string with it — every filter the reader had set, plus
 *     the query they were about to refine — because the href was built from
 *     `mode` alone.
 *
 * ## The shape
 *
 * `<form method="get" action={/{locale}/listings/{mode}} role="search">` around
 * an UNCONTROLLED `<input name={LISTINGS_FILTER_PARAM_NAMES.query}>` and a real
 * `type="submit"` glyph. With no JavaScript the browser submits it and the
 * server renders the results — the oldest progressive-enhancement shape there
 * is, and the one `QueryForm`/`NativeForm` already give the rest of this app.
 *
 * ⚠ UNCONTROLLED IS NOT AN IMPLEMENTATION DETAIL. `packages/ui`'s `Input` gates
 * a CONTROLLED field `disabled` until hydration (LET-78), and a disabled control
 * is NOT SUBMITTED — so a controlled field inside a native form submits an empty
 * query in exactly the window the form exists for. That is why the desktop
 * omnibox's `<Combobox action>` is a contract about destinations rather than a
 * no-JS search, and it is why this field reads its value from the URL as a
 * `defaultValue` and never as state.
 *
 * ## The filters survive the submit
 *
 * A GET form does not merge with the URL it was submitted from: the browser
 * builds the query string from the form's named controls and nothing else. So
 * every canonical filter the URL carries is restated as a hidden input, DERIVED
 * from the codec's own table (`listingsFilterCarriedFields`) rather than listed
 * here — a filter added to that table is carried by the next submit without
 * this file changing. `query` and `page` are the two the form owns: the reader
 * is typing the first, and a new query resets the second by definition.
 *
 * ## The overlay is the ENHANCEMENT
 *
 * Once hydrated, tapping the field opens the shared full-screen
 * {@link SearchOverlay} — the modern mobile pattern, and the surface the app
 * already had. Both native legs stay live underneath it: a keyboard reader who
 * tabs to the field types in place and presses Enter, and `onSubmit` commits the
 * SAME serialised fields the browser would have put in the URL as a client
 * navigation. Nothing here is reachable only through the overlay, which is the
 * property the old shape lacked.
 *
 * ## Why it owns the overlay
 *
 * The two placements — the home hero and the browse canvas's top bar — are in
 * different features, and the defect §3.6 replaced was precisely a trigger in
 * one place and its state in another (`ListingsClient.isSearchOpen`, whose
 * first shipped version was a no-op comment). Binding the two here makes "a
 * trigger wired to nothing" unrepresentable: there is no `onSearchClick` prop
 * to forget to pass, and a third placement needs no plumbing at all.
 *
 * ## Why it is a `*Form` and not a `*Field`
 *
 * `apps/public`'s ESLint rule #53 refuses any `FunctionDeclaration` whose name
 * ends in `Field` or `Input` outside `@letsgethome/ui/forms`, because that
 * vocabulary belongs to the design system's form primitives and an inline
 * component wearing the name is how a fourth `NumberField` gets written. This
 * is not one of those: it is a native `<form>` that happens to contain a field,
 * and its whole argument is the FORM. The name says so.
 *
 * ## Why the classes come from the primitive
 *
 * `comboboxFieldClassName(variant)` reads the PRIVATE `VARIANTS` geometry table
 * that the real `<ComboboxInput>` reads, so this field and the omnibox it stands
 * in for cannot drift in height, leading padding, radius or border — and
 * `ComboboxFieldSubmit` is the same COMPONENT the omnibox's submit glyph is, so
 * they cannot drift in glyph geometry either. A hand-copied
 * `rounded-full bg-background text-sm shadow-md` — which is what the hub's top
 * bar carried before §3.6 — is a fourth copy of that geometry held equal by
 * nothing.
 */
import { useLocalizedHref, useRouter, useSearchParams } from '@/shared/i18n/routing';
import { useTranslations } from '@/shared/i18n/useTranslations';
import {
  decodeListingsFilterParams,
  LISTINGS_FILTER_PARAM_NAMES,
  listingsFilterCarriedFields,
  routes,
  withQuery,
  type ListingsFilterParams,
  type ListingsMode,
} from '@letsgethome/routes';
import type { LocalizedText, PlaintextString } from '@letsgethome/types/branded';
import {
  comboboxFieldClassName,
  ComboboxFieldSubmit,
  type ComboboxVariant,
} from '@letsgethome/ui/combobox';
import { Input } from '@letsgethome/ui/input';
import { cn } from '@letsgethome/ui/lib/utils';
import { Search } from 'lucide-react';
import { useState, type SyntheticEvent } from 'react';

import { SearchOverlay } from './SearchOverlay';

/**
 * The vocabulary this FORM names, and therefore the vocabulary
 * `listingsFilterCarriedFields` must NOT restate as a hidden input: `query`
 * because the reader is typing it, `page` because a new query starts at the
 * first page. Everything else in the codec's table rides along.
 */
const FORM_OWNED_FILTERS: readonly (keyof ListingsFilterParams)[] = ['query', 'page'];

export interface SearchFormProps {
  /** Browse mode the form submits to, and the mode the overlay searches in. */
  readonly mode: ListingsMode;
  /**
   * What the EMPTY field prompts with — the hero's rotating example, or the
   * hub's "Search location…". Branded, so a raw English literal cannot be
   * passed: this is visible text, and the estate's untranslated-text gates
   * apply to it exactly as they do to the placeholder it imitates.
   *
   * The field's VALUE is deliberately NOT a prop: it is the query the URL
   * carries, read through the listings-filter codec. The hub used to hand it
   * down as `query`, which is a second copy of a fact the URL already states.
   */
  readonly placeholder: LocalizedText | PlaintextString;
  /** The field geometry to imitate. Defaults to the hero's. */
  readonly variant?: ComboboxVariant;
  readonly className?: string;
}

export function SearchForm({ mode, placeholder, variant = 'hero', className }: SearchFormProps) {
  const t = useTranslations('search');
  const router = useRouter();
  const searchParams = useSearchParams();
  // The GET target carries the URL locale — a bare `/listings/rent` would land
  // a no-JS submit on the default locale (the ADR 0142 class).
  const action = useLocalizedHref(routes.listings.browse(mode));
  const [open, setOpen] = useState(false);

  const { query } = decodeListingsFilterParams(searchParams);
  const carried = listingsFilterCarriedFields(searchParams, FORM_OWNED_FILTERS);

  /**
   * The SPA's enhancement over the browser's own submission: the SAME fields
   * the browser would have serialised into the URL, committed as a client
   * navigation. Reading them off the FORM rather than re-deriving them is what
   * makes the two legs incapable of disagreeing.
   */
  // `SyntheticEvent`, not the `FormEvent` alias: @types/react deprecates that
  // name outright ("FormEvent doesn't actually exist"), and the estate's
  // `no-deprecated` rule refuses it. `currentTarget` is the form either way.
  function handleSubmit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const submitted: Record<string, string> = {};
    for (const [name, value] of new FormData(event.currentTarget)) {
      if (typeof value === 'string' && value.trim() !== '') submitted[name] = value.trim();
    }
    setOpen(false);
    void router.push(withQuery(routes.listings.browse(mode), submitted));
  }

  return (
    <>
      <form
        method="get"
        action={action}
        role="search"
        onSubmit={handleSubmit}
        className="relative"
        // IDENTITY, the same stamp `QueryForm` carries, so a no-JS spec can name
        // THIS form rather than "the first search landmark CSS happens to show".
        // The hero server-renders the desktop combobox (hidden below `md` by a
        // class) AHEAD of this form in DOM order, and a role query only skips it
        // once the stylesheet has applied — measured 2026-09-19 (#7319 shard):
        // on a JS-off page read at `domcontentloaded`, `getByRole('search')`
        // resolved the gated combobox, whose input is `disabled`, for a full 5 s.
        data-query-form={routes.listings.browse(mode)}
      >
        {carried.map((field) => (
          <input key={field.name} type="hidden" name={field.name} value={field.value} readOnly />
        ))}

        <ComboboxFieldSubmit variant={variant} icon={<Search />} label={t('submit')} />

        <Input
          type="search"
          name={LISTINGS_FILTER_PARAM_NAMES.query}
          // UNCONTROLLED — see the module doc. `defaultValue`, so the field
          // shows what the URL says while `Input`'s hydration gate leaves it
          // enabled and therefore submittable.
          defaultValue={query ?? ''}
          // The placeholder is EXAMPLE text (「太古城」), not a name for the
          // control, so the accessible name is the search prompt — the same
          // sentence the omnibox's own `aria-label` carries.
          aria-label={t('placeholder')}
          placeholder={placeholder}
          enterKeyHint="search"
          autoComplete="off"
          // THE ENHANCEMENT. A tap on a phone opens the full-screen surface
          // instead of typing into a field under a 56px bar. Bound to the CLICK
          // and not to focus, deliberately: Radix returns focus here when the
          // overlay closes, and a focus-bound opener would reopen it forever.
          onClick={() => setOpen(true)}
          className={cn(comboboxFieldClassName(variant), className)}
        />
      </form>

      <SearchOverlay open={open} onOpenChange={setOpen} mode={mode} />
    </>
  );
}
